import { safeGet } from '../../../utils/safeGet'
import { filterByWord, filterByIssuerCode, getIssuersList } from '../../../utils/filters'
import { INIT_COVERAGE_FILTERS_EDIT } from '../../../settings/scoringProfilesSettings'

// TabProfileCoverage.vue is only reachable from the catalog-driven scoring profile pages
// (ScoringProfilesCreate.vue/ScoringProfilesEdit.vue), both of which pass useNewFilters=true,
// so the coverage refresh always goes through scoringProfileEditor.
function dispatchCoverageRefresh(dispatch, scoringProfileId) {
  return dispatch('scoringProfileEditor/loadProfileCoverage', scoringProfileId, { root: true })
}

export default {
  namespaced: true,

  state: {
    productsList: [],
    excludedProducts: [],
    filteredIncludedProducts: [],
    filteredExcludedProducts: [],
    defaultExcludedProducts: [],
    coverageFilters: INIT_COVERAGE_FILTERS_EDIT(),
    includedProductsNumber: 0,
    excludedProductsNumber: 0,
  },

  mutations: {
    setIncludedProductsNumber(state, payload) {
      state.includedProductsNumber = payload
    },

    setExcludedProductsNumber(state, payload) {
      state.excludedProductsNumber = payload
    },

    setProductsList(state, payload) {
      state.productsList = payload
    },

    setExcludedProducts(state, payload) {
      state.excludedProducts = payload
    },

    setFilteredIncludedProducts(state, payload) {
      state.filteredIncludedProducts = payload
    },

    setFilteredExcludedProducts(state, payload) {
      state.filteredExcludedProducts = payload
    },

    setDefaultExcludedProducts(state, payload) {
      state.defaultExcludedProducts = payload
    },

    setCoverageFilters(state, payload) {
      state.coverageFilters = payload
    },

    resetCoverageFilters(state) {
      state.coverageFilters = INIT_COVERAGE_FILTERS_EDIT()
    },
  },

  getters: {
    getFilteredIncludedProducts: (state) => {
      return safeGet(() => state.filteredIncludedProducts, [])
    },

    getFilteredExcludedProducts: (state) => {
      return safeGet(() => state.filteredExcludedProducts, [])
    },

    getCoverageExcludedProducts(state) {
      return safeGet(() => state.excludedProducts, [])
    },

    getDefaultExcludedProducts(state) {
      return safeGet(() => state.defaultExcludedProducts, [])
    },

    getCoverageProductsList(state) {
      return safeGet(() => state.productsList, [])
    },

    getExcludedProducts: (state) => {
      return safeGet(() => state.excludedProducts, [])
    },

    getCoverageFilters: (state) => {
      return safeGet(() => state.coverageFilters, INIT_COVERAGE_FILTERS_EDIT())
    },

    getCoverageIssuersList(state) {
      return getIssuersList(state.productsList)
    },

    getIncludedProductsNumber(state) {
      return safeGet(() => state.includedProductsNumber, 0)
    },

    getExcludedProductsNumber(state) {
      return safeGet(() => state.excludedProductsNumber, 0)
    },

    getExcludedProductsDiff(state, getters) {
      const excludedProducts = getters.getCoverageExcludedProducts
      const defaultExcludedProducts = getters.getDefaultExcludedProducts
      if (excludedProducts.length !== defaultExcludedProducts.length) return true
      if (excludedProducts.length === 0 && defaultExcludedProducts.length === 0) return false

      const list =
        excludedProducts.length <= defaultExcludedProducts.length
          ? { source: 'getDefaultExcludedProducts', target: 'getCoverageExcludedProducts' }
          : { source: 'getCoverageExcludedProducts', target: 'getDefaultExcludedProducts' }

      const diff = getters[list.source].every((k) => {
        getters[list.target].find((j) => j.productName === k.productName)
      })
      return diff
    },
  },

  actions: {
    setUpProductsList({ commit, dispatch }, payload) {
      //Products received from response
      commit('setProductsList', payload.productsToSave)
      commit('setExcludedProducts', payload.excludedProducts)
      commit('setIncludedProductsNumber', payload.productsToSave.length - payload.excludedProducts.length)
      commit('setExcludedProductsNumber', payload.excludedProducts.length)
      dispatch('searchProducts')
    },

    setDefaultExcludedProducts({ commit }, value) {
      commit('setDefaultExcludedProducts', value)
    },

    setCoverageFilters({ commit }, value) {
      commit('setCoverageFilters', value)
    },

    resetCoverageFilters({ commit }) {
      commit('resetCoverageFilters')
    },

    searchProducts({ state, commit, getters }) {
      let availableProducts = state.productsList
      let filters = getters.getCoverageFilters

      if (filters.queryString && filters.queryString !== '') {
        availableProducts = filterByWord(availableProducts, filters.queryString)
      }

      if (filters.issuers.length > 0) {
        availableProducts = filterByIssuerCode(availableProducts, filters.issuers)
      }

      const includedProduct = availableProducts.filter((el) => el.isExcludedFromPerimeter === false)
      const excludedProduct = availableProducts.filter((el) => el.isExcludedFromPerimeter)

      commit('setFilteredIncludedProducts', includedProduct)
      commit('setFilteredExcludedProducts', excludedProduct)
    },

    includeCoverageProducts({ commit, dispatch, getters, rootGetters }, { products } = {}) {
      const excluded = getters.getCoverageExcludedProducts
      if (excluded.length === 0) return

      let excludedList = []
      let productsList = getters.getCoverageProductsList

      const idsToInclude = products

      productsList.map((el) => {
        if (idsToInclude.includes(el.productId)) {
          el.isExcludedFromPerimeter = false
        }
      })

      productsList.forEach((el) => {
        if (el.isExcludedFromPerimeter) {
          excludedList.push({ issuerCode: el.issuerCode, productName: el.productName })
        }
      })

      const scoringProfileId = rootGetters['scoringProfilesCommon/getScoringProfileId']

      commit('setProductsList', productsList)
      commit('setExcludedProducts', excludedList)
      commit('setIncludedProductsNumber', productsList.length - excludedList.length)
      commit('setExcludedProductsNumber', excludedList.length)
      dispatchCoverageRefresh(dispatch, scoringProfileId)
    },

    excludeCoverageProducts({ commit, dispatch, getters, rootGetters }, { products } = {}) {
      let productsList = getters.getCoverageProductsList
      const idsToExclude = products

      productsList.map((el) => {
        if (idsToExclude.includes(el.productId)) {
          el.isExcludedFromPerimeter = true
        }
      })

      let excludedList = []

      productsList.forEach((el) => {
        if (el.isExcludedFromPerimeter) {
          excludedList.push({ issuerCode: el.issuerCode, productName: el.productName })
        }
      })

      const scoringProfileId = rootGetters['scoringProfilesCommon/getScoringProfileId']

      commit('setProductsList', productsList)
      commit('setExcludedProducts', excludedList)
      commit('setIncludedProductsNumber', productsList.length - excludedList.length)
      commit('setExcludedProductsNumber', excludedList.length)
      dispatchCoverageRefresh(dispatch, scoringProfileId)
    },
  },
}
